interface TokenData {
	used: number
	date: string
}

interface Account {
	account_type?: string
	features?: string[]
}

class TokenService {
	private readonly STORAGE_KEY = 'ai_generation_tokens'

	private getCurrentDate(): string {
		return new Date().toISOString().split('T')[0]
	}

	private getTokenData(): TokenData {
		const stored = localStorage.getItem(this.STORAGE_KEY)
		const currentDate = this.getCurrentDate()

		if (stored) {
			const data = JSON.parse(stored) as TokenData
			if (data.date !== currentDate) {
				return { used: 0, date: currentDate }
			}
			return data
		}

		return { used: 0, date: currentDate }
	}

	private saveTokenData(data: TokenData): void {
		localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data))
	}

	getMaxTokens(account?: Account | null): number {
		if (!account) return 5

		if (account.features?.includes('ai_unlimited')) {
			return 999
		}

		if (
			account.account_type === 'self managed' ||
			account.account_type === 'lite' ||
			account.account_type === 'essential'
		) {
			return 5
		}

		return 20
	}

	getUsedTokens(): number {
		const data = this.getTokenData()
		return data.used
	}

	getAvailableTokens(account?: Account | null): number {
		const max = this.getMaxTokens(account)
		const used = this.getUsedTokens()
		return Math.max(0, max - used)
	}

	useToken(account?: Account | null): boolean {
		const available = this.getAvailableTokens(account)

		if (available <= 0) {
			return false
		}

		const data = this.getTokenData()
		data.used += 1
		data.date = this.getCurrentDate()
		this.saveTokenData(data)

		return true
	}

	getNextResetDate(): Date {
		const tomorrow = new Date()
		tomorrow.setDate(tomorrow.getDate() + 1)
		tomorrow.setHours(0, 0, 0, 0)
		return tomorrow
	}

	getResetDateDisplay(): string {
		const resetDate = this.getNextResetDate()
		const day = resetDate.getDate()
		const month = resetDate.toLocaleDateString('en-US', { month: 'short' })

		const suffix =
			day === 1 || day === 21 || day === 31
				? 'st'
				: day === 2 || day === 22
					? 'nd'
					: day === 3 || day === 23
						? 'rd'
						: 'th'

		return `${day}${suffix} ${month}.`
	}

	clearTokenData(): void {
		localStorage.removeItem(this.STORAGE_KEY)
	}
}

export const tokenService = new TokenService()
